/**
 * TikTok OAuth Credentials
 *
 * Reads the TikTok developer app credentials from the system_settings table.
 * The app secret is stored encrypted via lib/encryption.ts (AES-256-GCM).
 */

import type { SupabaseClient } from "@/lib/supabase"
import { decrypt } from "@/lib/encryption"

export interface TiktokOAuthCredentials {
  /**
   * TikTok's App ID. The same value appears as `client_key` in webhook payloads,
   * `app_id` in the webhook subscription call and `client_id` in the token call
   * — one credential, four names. Copy it from My Apps → App Detail → Basic
   * Information.
   */
  appId: string
  /** App secret — also the token call's `client_secret` and the webhook HMAC key. */
  appSecret: string
  /**
   * TikTok issues a fixed per-app "TikTok account holder authorization URL"; it
   * cannot be composed from app id + scope + redirect_uri, only copied from the
   * app dashboard. We append `state` and `disable_auto_auth` to it.
   */
  authorizationUrl: string
}

type KVSetting = { key: string; value: string | null }

export async function getTiktokCredentials(
  supabase: SupabaseClient,
): Promise<TiktokOAuthCredentials> {
  const { data, error } = await supabase
    .from("system_settings")
    .select("key,value")
    .in("key", ["tiktok_app_id", "tiktok_app_secret", "tiktok_authorization_url"])

  if (error) {
    throw new Error(`Failed to fetch TikTok credentials: ${error.message}`)
  }

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error(
      "TikTok OAuth credentials not found in system_settings. Configure them in System Settings.",
    )
  }

  const map = new Map((data as KVSetting[]).map((row) => [row?.key, row?.value ?? ""]))

  const appId = map.get("tiktok_app_id") || ""
  const appSecretEnc = map.get("tiktok_app_secret") || ""
  const authorizationUrl = map.get("tiktok_authorization_url") || ""

  if (!appId || !appSecretEnc || !authorizationUrl) {
    throw new Error(
      "TikTok OAuth credentials are incomplete. Configure App ID, App Secret and the account holder authorization URL in System Settings.",
    )
  }

  const appSecret = decrypt(appSecretEnc)

  if (!appSecret) {
    throw new Error(
      "TikTok App Secret is empty after decryption. Re-enter the secret in System Settings.",
    )
  }

  return { appId, appSecret, authorizationUrl }
}
